/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { AutomationRuleActivityContextType, AutomationRuleActivityType, Item } from './types';
import { searchIdentities } from 'src/azure/services/get-board-users';
import { getAutomationActivitiesService } from 'src/services/appgami-api/get-automation-rule-activity-service';
import { useAzureProvider } from 'src/azure/context';
import DetailsDialog from './details-dialog';
import { IIdentity } from 'azure-devops-extension-api/Identities';

const MICROSOFT_ENTRA_IMAGE_URL: string = process.env.REACT_APP_MICROSOFT_ENTRA_IMAGE_URL || '';

const AutomationRuleActivityContext = createContext({} as AutomationRuleActivityContextType);
export const useAutomationRuleActivity = () => useContext(AutomationRuleActivityContext);

export default function AutomationRuleActivityProvider(props: { children?: ReactNode }) {
	const {applicationContext} = useAzureProvider();
	const [openDetails, setOpenDetails] = useState(false);
	const [details, setDetails] = useState<null | any>(null);

	// filter
	const [state, setState] = useState('');
	const [time, setTime] = useState('');

	const [loadingList, setLoadingList] = useState(false);
	const [lastActivityId, setLastActivityId] = useState<string | null>(null);
	const [list, setList] = useState<Array<Item>>([]);

	const setAutomationRuleActivityList = useCallback(async (args: {
    oldActivityList: Array<Item>,
    lastActivityId: string | null;
    pageSize: string,
    lastTimeFilter?: string;
		status?: string;
  }) => {
		try {
			const response = await getAutomationActivitiesService({
				organizationName: applicationContext.organizationName,
				projectId: applicationContext.projectId,
				automationId: null,
				lastItem: args.lastActivityId,
				pageSize: args.pageSize,
				lastTime: args.lastTimeFilter,
				status: args.status,
			});
  
			if (response.status === 200) {
				setLastActivityId(response.data.lastItem);
				const data = response.data.automationRuleLogCollection as Array<AutomationRuleActivityType>;
				const mapUsers = new Map<string, { name: string; image: string }>();
				const identityIds = data.map((item) => item.executed_by);
  
				await Promise.all(identityIds.map((item) => new Promise((resolve, reject) => {
					if (item.actor === 'service-principal') {
						mapUsers.set(item.id, { name: 'Appgami Automation', image: MICROSOFT_ENTRA_IMAGE_URL });
						resolve(null);
					}

					if (!mapUsers.has(item.id) && item.actor === 'user') {
						searchIdentities(item.id, { queryTypeHint: 'uid' })
							.then((identities) => {
								const found: any = identities.find(identity => (identity as IIdentity & { localId: string }).localId === item.id);
								mapUsers.set(item.id, { name: found.displayName, image: `https://dev.azure.com/${found.image}` });
								resolve(null);
							})
							.catch(() => {
								reject();
							});
					}
				})));
  
				const newActivityList = data.filter(activityItem => !args.oldActivityList.some(item => item.activityData.id === activityItem.id));
				const newResultList: Array<Item> = newActivityList.map((item) => {
					const hookData = JSON.parse(item.service_hook_data);
					const wiId = hookData.eventType === 'workitem.created' ? hookData.resource.id : hookData.resource.workItemId;
					const foundUser = mapUsers.get(item.executed_by.id);
      
					return {
						activityData: item,
						firstColumn: {
							name: item.automation_ref.name,
							executedOnTime: item.executed_at,
							timeTaken: item.executed_on_time
						},
						id: {
							label: wiId,
							url: hookData.resource._links.html.href
						},
						status: {
							label: item.status
						},
						executedBy: {
							avatar: foundUser?.image || '',
							name: foundUser?.name || ''
						}
					};
				});
        
				setList(() => [...args.oldActivityList, ...newResultList]);
			}
		} catch (err) {
			console.error(err);
		}
	}, [applicationContext]);

	const loadInitialActivityList = useCallback(async () => {
		try {
			setLoadingList(true);
			await setAutomationRuleActivityList({
				oldActivityList: [],
				lastActivityId: null,
				pageSize: '15',
			});
			setLoadingList(false);
		} catch (err) {
			setLoadingList(false);
		}
	}, []);

	const getListByFilter = useCallback(async (args: {
		oldActivityList: Array<Item>,
    automationRuleId: string | null;
    lastActivityId: string | null;
    lastTimeFilter: string;
		status?: string;
	}) => {
		await setAutomationRuleActivityList({
			...args,
			lastTimeFilter: args.lastTimeFilter !== 'anytime' ? args.lastTimeFilter : undefined,
			pageSize: '15'
		});
	}, []);

	const openDetailsHandler = useCallback((activityId?: string) => {
		if (activityId) {
			const found = list.find(item => item.activityData.id === activityId);
			if (found) {
				setDetails(found.activityData.service_hook_data);
			}
		}

		setOpenDetails(oldValue => !oldValue);
	}, [list]);

	return (
		<AutomationRuleActivityContext.Provider value={{
			list: list,
			loadInitialActivityList,
			loadingList,
			lastActivityId,
			getListByFilter,
			openDetails,
			openDetailsHandler,
			details,
			filter: {
				time,
				setTime,
				state,
				setState
			}
		}}>
			{props.children}
			<>
				<DetailsDialog />
			</>
		</AutomationRuleActivityContext.Provider>
	);
}